/* eslint-disable max-len */
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { ClickOutsideModule } from 'ng-click-outside';
import { DndModule } from 'ngx-drag-drop';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';

import { TimeHelper } from '../Forms/helpers/time.helper';
import { MiscModule } from '../Misc/misc.module';
import { ReportsApi } from '../../api/reports/reports.api';
import { SharedReportApi } from '../../api/reports/shared-report.api';

import { UsersService } from '../../Core/Users/services/users.service';

import { PageBuilderModule } from '../PageBuilder/page-builder.module';
import { CommonDialogsModule } from '../Dialogs/common-dialogs.module';
import { LocalizationModule } from '../../Core/Localization/localization.module';
import { ChartsModule } from '../Charts/charts.module';
import { CommonFormsModule } from '../Forms/forms.module';
import { LayoutModule } from '../Layout/layout.module';
import { FilterModule } from '../Filter/filter.module';
import { CoreDialogsModule } from '../../Core/Dialogs/dialogs.module';
import { CommonBasicModule } from '../Basic/basic.module';
import { MailingListsModule } from '../MailingLists/mailing-lists.module';
import { TargetingSharedModule } from '../TargetingShared/targeting-shared.module';
import { MentionsModule } from '../Mentions/mentions.module';
import { MonitorSharedModule } from '../../Monitor/Shared/monitoring-shared.module';
import { ContentLibraryDialogModule } from '../ContentLibrary/content-library.module';

// This module
import { ReportComponent } from './components/Report/report.component';
import { ChartBlockComponent } from './components/ChartBlock/chart-block.component';
import { ReportsService } from './services/reports.service';
import { ChartSetupDialogComponent } from './components/ChartSetupDialog/chart-setup-dialog.component';
import { DateRangeFieldComponent } from './components/DateRangeField/date-range-field.component';
import { SeriesPickerFieldComponent } from './components/SeriesPickerField/series-picker-field.component';
import { ReportsSeriesService } from './services/reports-series.service';
import { ReportsSeriesMapperService } from './services/reports-series-mapper.service';
import { ReportsSeriesPreviewService } from './services/report-series-preview.service';
import { CoverageSummaryChartBlockComponent } from './components/CoverageSummaryChartBlock/coverage-summary-chart-block.component';
import { ReportsChartOptionsService } from './services/reports-chart-options.service';
import { RegionPickerFieldComponent } from './components/RegionPickerField/region-picker-field.component';
import { RegionPickerFieldService } from './services/region-picker-field.service';
import { SeriesPickerItemComponent } from './components/SeriesPickerItem/series-picker-item.component';
import { SeriesPickerCompareFieldComponent } from './components/SeriesPickerCompareField/series-picker-compare-field.component';
import { ReportCreateDialogComponent } from './components/ReportCreateDialog/report-create-dialog.component';
import { SharedReportsComponent } from './components/SharedReportsComponent/shared-reports.component';
import { SharedReportService } from './services/shared-report.service';
import { SharedReportCardComponent } from './components/SharedReportCard/shared-report-card.component';
import { SharedReportSearchService } from './services/shared-report-search.service';
import { UpdateGlobalSourceDialogComponent } from './components/updateGlobalSourceDialog/update-global-source-dialog.component';
import { SharedReportCreateDialogComponent } from './components/SharedReportCreateDialog/shared-report-create-dialog.component';
import { ChartCardComponent } from './components/ChartCard/chart-card.component';
import { SharedReportInviteDialogComponent } from './components/SharedReportInviteDialog/shared-report-invite-dialog.component';
import { ReportsPageBuilderService } from './services/reports-page-builder.service';
import { ExternalDataApi } from '../../api/externalData/external-data.api';
import { ExternalDataSourcePickerFieldComponent } from './components/ExternalDataSourcePickerField/external-data-source-picker-field.component';
import { ReportsExternalDataMapperService } from './services/report-external-data-mapper.service';
import { SharedReportEditDialogComponent } from './components/SharedReportEditDialog/shared-report-edit-dialog.component';
import { ExternalDataSourceDateRangeFieldComponent } from './components/ExternalDataSourceDateRangeField/external-data-source-date-range-field.component';
import { PREventsFieldComponent } from './components/PREventsField/pr-events-field.component';
import { ProminenceGroupPickerComponent } from './components/ProminenceGroupPicker/prominence-group-picker.component';
import { SeriresCountFieldComponent } from './components/SeriesCountField/series-count-field.component';
import { ListChartBlockComponent } from './components/ListChartBlock/list-chart-block.component';
import { ListDetailCardComponent } from './components/ListChartBlock/ListDetailCard/list-detail-card.component';
import { ListStackedBarCardComponent } from './components/ListChartBlock/ListStackedbarCard/list-stacked-bar-card.component';
import { GenerateReportDialogComponent } from './components/GenerateReportDialog/generate-report-dialog.component';
import { SharedReportsExportComponent } from './components/SharedReportsComponent/SharedReportsExportComponent/shared-reports-export.component';
import { ReportsChartDetailService } from './services/reports-chart-detail.service';
import { ChartDataDisplaySettingsCheckboxFieldComponent} from './components/ChartDataDisplaySettingsCheckboxFieldComponent/chart-data-display-settings-checkbox-field.component';
import { MetricPickerComponent } from './components/MetricPickerComponent/metric-picker.component';
import { MultipleMetricsPickerComponent } from './components/MultipleMetricsPickerComponent/multiple-metrics-picker.component';
import { TopTermChartBlockComponent } from './components/TopTermChartBlock/top-term-chart-block.component';
import { TermTypesRadioButtonsFieldComponent } from './components/TermTypesRadioButtonsFieldComponent/term-types-radio-buttons-field.component';
import { ChartNetworkCheckboxFieldComponent } from './components/ChartNetworkSelectionCheckboxFieldComponent/chart-network-checkbox-field.component';
import { ChartDisplaySettingsFieldComponent } from './components/ChartDisplaySettingsField/chart-display-settings-field.component';
import { TakeOverModule } from '../TakeOver/take-over.module';
import { RhpContextsModule } from '@Common/RhpContexts/rhp-contexts.module';
import { DateRangeFieldNoSeriesComponent } from './components/DateRangeNoSeries/date-range-field-no-series.component';
import { TimeIntervalDropdownFieldComponent } from './components/TimeIntervalDropdownField/time-interval-dropdown-field.component';
import { TopMentionsChartBlockComponent } from './components/TopMentions/TopMentionsChartBlock/top-mentions-chart-block.component';
import { TopMentionsMetadataFieldComponent } from './components/TopMentions/TopMentionsMetadataField/top-mentions-metadata-field.component';
import { TopMentionsSortFieldComponent } from './components/TopMentions/TopMentionsSortField/top-mentions-sort-field.component';
import { TopCuratedMentionsFieldComponent } from './components/TopMentions/TopCuratedMentionsField/top-curated-mentions-field.component';
import { ReportImportMentionDialogComponent } from './components/ReportImportMentionDialog/report-import-mention-dialog.component';
import { TopPostChartBlockComponent } from './components/TopPostChartBlock/top-post-chart-block.component';
import { TopPostsChartComponent } from './components/TopPostsChart/top-posts-chart.component';
import { PostFeedCardComponent } from './components/PostFeedCard/post-feed-card.component';
import { TopMentionsChartComponent } from './components/TopMentions/TopMentionsChart/top-mentions-chart.component';
import { TopMentionsChartCardComponent } from './components/TopMentions/TopMentionsChartCard/top-mentions-chart-card.component';
import { ReportsChartSeriesConverterService } from './services/reports-chart-series-converter.service';
import { ReportsSeriesDataMapperService } from './services/reports-series-data-mapper.service';
import { ReportsSeriesPreviewNewService } from './services/reports-series-preview-new.service';
import { ChartDrillDownDialogComponent } from './components/ChartDrillDownDialog/chart-drill-down-dialog.component';
import { ChartDrilldownSourcePickerComponent } from './components/ChartDrilldownSourcePicker/chart-drilldown-source-picker.component';
import { CoverageInsightComponent } from './components/CoverageInsight/coverage-insight.component';
import { CoverageAnalysisService } from '../../Monitor/Shared/services/coverage-analysis.service';
import { CoverageMetricChartDisplaySettingsFieldComponent } from './components/CoverageMetricChartDisplaySettingsField/coverage-metric-chart-display-settings-field.component';
import { CoverageMetricChartBlockComponent } from './components/CoverageMetricChartBlock/coverage-metric-chart-block.component';
import { ActionAssistantComponent } from './components/ActionAssistant/action-assistant.component';
import { Am5ChartBlockComponent } from './components/Am5ChartBlock/am5-chart-block.component';
import { Am5ChartConfigurationService } from './services/am5-chart-configuration.service';
import { Am5ChartExportLabelService } from './services/am5-chart-export-label.service';
import { Am5ChartResponsiveRulesService } from './services/am5-chart-responsive-rules.service';
import { Am5ChartSettingsService } from './services/am5-chart-settings.service';

// TODO future sprint only
// import { SharedReportRecipientCardComponent } from './components/SharedReportRecipientCard/shared-report-recipient-card.component';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SelectModule,
        TooltipModule,
        PageBuilderModule,
        LocalizationModule,
        AutoCompleteModule,
        CommonDialogsModule,
        CommonFormsModule,
        ClickOutsideModule,
        MiscModule,
        ChartsModule,
        InputTextModule,
        LayoutModule,
        FilterModule,
        CoreDialogsModule,
        CommonBasicModule,
        RouterModule,
        MailingListsModule,
        TargetingSharedModule,
        DatePickerModule,
        ProgressBarModule,
        MentionsModule,
        MonitorSharedModule,
        TakeOverModule,
        DndModule,
        RhpContextsModule,
        ContentLibraryDialogModule
    ],
    declarations: [
        ReportComponent,
        ChartBlockComponent,
        Am5ChartBlockComponent,
        CoverageSummaryChartBlockComponent,
        ChartSetupDialogComponent,
        DateRangeFieldComponent,
        SeriresCountFieldComponent,
        SeriesPickerFieldComponent,
        SeriesPickerItemComponent,
        RegionPickerFieldComponent,
        SeriesPickerCompareFieldComponent,
        ReportCreateDialogComponent,
        SharedReportsComponent,
        SharedReportsExportComponent,
        SharedReportCardComponent,
        SharedReportCreateDialogComponent,
        ChartCardComponent,
        SharedReportInviteDialogComponent,
        // TODO future sprint only
        // SharedReportRecipientCardComponent
        UpdateGlobalSourceDialogComponent,
        MetricPickerComponent,
        MultipleMetricsPickerComponent,
        ExternalDataSourcePickerFieldComponent,
        SharedReportEditDialogComponent,
        ExternalDataSourceDateRangeFieldComponent,
        PREventsFieldComponent,
        ProminenceGroupPickerComponent,
        ListChartBlockComponent,
        ListDetailCardComponent,
        ListStackedBarCardComponent,
        GenerateReportDialogComponent,
        ChartDataDisplaySettingsCheckboxFieldComponent,
        TopTermChartBlockComponent,
        TermTypesRadioButtonsFieldComponent,
        ChartNetworkCheckboxFieldComponent,
        TimeIntervalDropdownFieldComponent,
        ChartDisplaySettingsFieldComponent,
        CoverageMetricChartDisplaySettingsFieldComponent,
        DateRangeFieldNoSeriesComponent,
        TopMentionsChartBlockComponent,
        TopMentionsSortFieldComponent,
        TopMentionsMetadataFieldComponent,
        TopCuratedMentionsFieldComponent,
        ReportImportMentionDialogComponent,
        TopPostChartBlockComponent,
        PostFeedCardComponent,
        TopPostsChartComponent,
        TopMentionsChartComponent,
        TopMentionsChartCardComponent,
        ChartDrillDownDialogComponent,
        ChartDrilldownSourcePickerComponent,
        CoverageInsightComponent,
        CoverageMetricChartBlockComponent,
        ActionAssistantComponent
    ],
    exports: [
        ReportComponent,
        ChartBlockComponent,
        Am5ChartBlockComponent,
        CoverageSummaryChartBlockComponent,
        ChartSetupDialogComponent,
        DateRangeFieldComponent,
        SeriesPickerFieldComponent,
        SeriresCountFieldComponent,
        RegionPickerFieldComponent,
        SharedReportsComponent,
        PREventsFieldComponent,
        ProminenceGroupPickerComponent,
        ListChartBlockComponent,
        ListDetailCardComponent,
        ListStackedBarCardComponent,
        GenerateReportDialogComponent,
        SharedReportsExportComponent,
        MultipleMetricsPickerComponent,
        TopTermChartBlockComponent,
        TermTypesRadioButtonsFieldComponent,
        TimeIntervalDropdownFieldComponent,
        ChartDisplaySettingsFieldComponent,
        CoverageMetricChartDisplaySettingsFieldComponent,
        DateRangeFieldNoSeriesComponent,
        TopMentionsChartBlockComponent,
        TopMentionsSortFieldComponent,
        TopMentionsMetadataFieldComponent,
        TopCuratedMentionsFieldComponent,
        ReportImportMentionDialogComponent,
        TopPostChartBlockComponent,
        PostFeedCardComponent,
        TopPostsChartComponent,
        TopMentionsChartComponent,
        TopMentionsChartCardComponent,
        ChartDrilldownSourcePickerComponent,
        CoverageMetricChartBlockComponent,
        ActionAssistantComponent
    ],
    providers: [
        ReportsApi,
        ReportsService,
        {
            provide: 'ReportsService',
            useExisting: ReportsService
        },
        ReportsSeriesMapperService,
        ReportsSeriesPreviewService,
        ReportsSeriesService,
        TimeHelper,
        ReportsChartOptionsService,
        RegionPickerFieldService,
        SharedReportApi,
        SharedReportService,
        UsersService,
        SharedReportSearchService,
        ReportsPageBuilderService,
        ExternalDataApi,
        ReportsExternalDataMapperService,
        ReportsChartDetailService,
        {
            provide: 'ReportsDrillDownInterface',
            useExisting: ReportsChartDetailService
        },
        ReportsChartSeriesConverterService,
        ReportsSeriesDataMapperService,
        ReportsSeriesPreviewNewService,
        CoverageAnalysisService,
        Am5ChartConfigurationService,
        Am5ChartExportLabelService,
        Am5ChartSettingsService,
        Am5ChartResponsiveRulesService
    ]
})
export class ReportsModule { }
